import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {ReactComponent as TradlySidebarLogo} from '../assets/tradlySidebarLogo.svg';
import {fixed_menus} from '../Constants/SideMenusArray';
import SideMenu from './SideMenu/SideMenu';
import {ICONS} from '../Constants/Icons';
import {ChevronDoubleLeftIcon, ChevronDoubleRightIcon, MagnifyingGlassIcon, ViewColumnsIcon, ChevronUpDownIcon, UserPlusIcon, QuestionMarkCircleIcon, Cog6ToothIcon, PlusIcon, XMarkIcon, ChevronDownIcon} from '@heroicons/react/24/outline';
import {useNavigate} from 'react-router-dom';
import {getConfigsByKey} from '../Settings/Actions/getConfigsByKey';
import {SaveConfigsDataByKey} from '../Settings/Actions/SaveConfigsDataByKey';
import TopSearcher from './Header/TopSearcher';
import Modal from '../Component/UI/modals/Modal';
import TenantsList from '../Component/SiteList/TenantsList';
import AppConstants from '../Constants/AppConstants';
import {B2CMenus} from '../Constants/B2CMenus';
import {AllMenus} from '../Constants/AllMenus';
import {CONFIG_CONSTANT} from '../Constants/ConfigsConstants';
// Copilot is temporarily disabled until the Ask Tradly AI experience is ready.
// import Copilot from '../Copilot/Copilot';
import {useTheme} from '../HelperClasses/ThemeContext';
import AICreditsControl from '../Component/AICreditsControl';
import MenuIcon from './SideMenu/MenuIcon';
import {translateMenuTitle} from './SideMenu/menuText';
import {useLocale} from '../Localization/LocaleContext';

const CommerceAccordion = ({item, isExpand, text_color, background_color}) => {
  const [open, setOpen] = useState(false);
  const {translate} = useLocale();
  return (
    <div>
      <div
        className="group relative flex items-start justify-start cursor-pointer"
        onClick={() => setOpen(v => !v)}>
        <div
        className={`dark-side-menu w-full relative flex min-h-[34px] items-center py-1 bg-transparent group-hover:bg-white/10 rounded-lg transition-all duration-150 ${
          isExpand ? 'justify-start gap-3 px-3 mx-1.5' : 'justify-center gap-0 px-0 mx-0'
        }`}
          style={{color: text_color}}>
          <span style={{opacity: 0.75, display: 'flex', flexShrink: 0}}>
            <MenuIcon title={item.title} size={16} color={text_color} />
          </span>
          {isExpand && (
            <>
              <p className="side-menu-label opacity-100 block text-sm font-semibold leading-5 flex-1">
                {translateMenuTitle(item, translate)}
              </p>
              <ChevronDownIcon
                style={{
                  width: 13,
                  height: 13,
                  opacity: 0.6,
                  flexShrink: 0,
                  transition: 'transform 150ms',
                  transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                }}
              />
            </>
          )}
        </div>
      </div>
      {open && (
        <div className={isExpand ? 'pl-3' : ''}>
          {item.children?.map((child, i) => (
            <SideMenu
              key={child.key_name || child.id || i}
              menu={child}
              isExpand={isExpand}
              text_color={text_color}
              background_color={background_color}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const COMMERCE_MENU_KEYS = new Set([
  'orders',
  'listing_section',
  'marketing_section',
  'report_section',
  'finance_section',
  'communication',
]);

const MARKETPLACE_MENU_KEYS = new Set(['account_section', 'commissions']);

const CHANNEL_MENU_KEYS = new Set([
  'web_section',
  'app_section',
  'agents_section',
  'vibe_coding_section',
  'mcp_section',
  'opensource_starter_kits',
]);

const addPresentationGroups = menus => {
  const marketplaceItems = menus.filter(
    menu => MARKETPLACE_MENU_KEYS.has(menu?.key_name) && menu?.isShow !== false,
  );
  const commerceItems = menus.filter(
    menu => COMMERCE_MENU_KEYS.has(menu?.key_name) && menu?.isShow !== false,
  );
  const channelItems = menus.filter(
    menu => CHANNEL_MENU_KEYS.has(menu?.key_name) && menu?.isShow !== false,
  );
  const channelGroup = menus.find(menu => menu?.key_name === 'channels_group');

  const visibleMenus = menus.filter(
    menu =>
      !COMMERCE_MENU_KEYS.has(menu?.key_name) &&
      !MARKETPLACE_MENU_KEYS.has(menu?.key_name) &&
      !CHANNEL_MENU_KEYS.has(menu?.key_name) &&
      menu?.key_name !== 'channels_group',
  );
  const appsIndex = visibleMenus.findIndex(menu => menu?.key_name === 'apps_group');
  const scheduleIndex = visibleMenus.findIndex(menu => menu?.key_name === 'schedule');
  const firstPresentationIndex = appsIndex >= 0
    ? appsIndex + 1
    : scheduleIndex >= 0
    ? scheduleIndex + 1
    : visibleMenus.length;

  let nextPresentationIndex = firstPresentationIndex;
  if (marketplaceItems.length > 0) {
    visibleMenus.splice(nextPresentationIndex, 0, {
      id: 'marketplace-presentation',
      key_name: 'marketplace-presentation',
      title: 'Marketplace',
      type: 'presentation_group',
      isShow: true,
      children: marketplaceItems,
    });
    nextPresentationIndex += 1;
  }

  if (commerceItems.length > 0) {
    visibleMenus.splice(nextPresentationIndex, 0, {
      id: 'commerce-presentation',
      key_name: 'commerce-presentation',
      title: 'Commerce',
      type: 'presentation_group',
      isShow: true,
      children: commerceItems,
    });
  }

  // Channels is a section heading for the channel destinations, and follows
  // the Apps section (Commerce, Marketing, Intelligence, Subscription).
  if (channelGroup && channelItems.length > 0) {
    const separatorIndex = visibleMenus.findIndex(
      menu => menu?.key_name === 'undefined' || menu?.id === 17,
    );
    const channelsIndex = separatorIndex >= 0 ? separatorIndex : visibleMenus.length;
    visibleMenus.splice(channelsIndex, 0, channelGroup, ...channelItems);
  }

  return visibleMenus;
};

const MINI_APPS = [
  {
    key_name: 'workflow',
    title: 'Marketing',
    description: 'Run email campaigns, push notifications, and promotions to grow your audience.',
    icon: '📣',
    why: [
      { icon: '✉️', text: 'Send targeted email campaigns to buyers and sellers based on activity' },
      { icon: '🔔', text: 'Push notifications to re-engage users who haven\'t visited in 7+ days' },
      { icon: '🎯', text: 'Run promotions with coupon drops timed to your seasonal events' },
    ],
    pricing: { model: 'credits', label: 'Credits-based', detail: 'Pay per send — 1,000 emails costs 100 credits. Credits never expire.' },
  },
  {
    key_name: 'intelligence_section',
    title: 'Intelligence',
    description: 'Understand your growth with analytics, funnels, and AI-powered insights.',
    icon: '✦',
    why: [
      { icon: '📊', text: 'See which listings, accounts, and categories drive the most GMV' },
      { icon: '🔍', text: 'Drop-off funnels show exactly where buyers leave before checkout' },
      { icon: '🤖', text: 'AI surfaces anomalies and opportunities you\'d miss in raw data' },
    ],
    pricing: { model: 'credits', label: 'Credits-based', detail: 'AI analysis and generated reports consume credits. Data views are always free.' },
  },
  {
    key_name: 'platform_health',
    title: 'Auth',
    description: 'Monitor authentication activity, sign-in health, and identity configuration.',
    icon: '🔐',
    optIn: true,
    why: [
      { icon: '🔑', text: 'Review sign-in and sign-up activity across your marketplace' },
      { icon: '📊', text: 'Spot successful and failed authentication trends over time' },
      { icon: '⚙️', text: 'Manage the authentication providers connected to your marketplace' },
    ],
    pricing: { model: 'connect', label: 'Included', detail: 'Authentication monitoring is available as a dashboard mini app.' },
  },
  {
    key_name: 'schedule',
    title: 'Bookings Schedule',
    description: 'Manage booking availability and scheduled orders from one calendar.',
    icon: '📅',
    optIn: true,
    why: [
      { icon: '🗓️', text: 'See bookings and scheduled orders in month, week, or day views' },
      { icon: '🔎', text: 'Open booking details and jump directly to the related order' },
      { icon: '📍', text: 'Keep scheduled marketplace activity visible to your operations team' },
    ],
    pricing: { model: 'connect', label: 'Included', detail: 'Bookings Schedule is available as a dashboard mini app.' },
  },
  {
    key_name: 'subscription_section',
    title: 'Subscription',
    description: 'Manage plans, billing cycles, and feature entitlements for your tenants.',
    icon: '💳',
    why: [
      { icon: '🗂️', text: 'Create tiered plans (Free / Pro / Enterprise) with per-feature gates' },
      { icon: '🔄', text: 'Automate billing cycles — monthly, annual, or custom durations' },
      { icon: '🔑', text: 'Entitlements unlock or restrict features across your tenant base' },
    ],
    pricing: { model: 'connect', label: 'Connect', detail: 'Task-based pricing — charged per subscription event (upgrade, renewal, cancellation).' },
  },
  {
    key_name: 'membership_section',
    title: 'Membership',
    description: 'Manage membership programs, enrolled members, contributions, and payment dues.',
    icon: '🎟️',
    optIn: true,
    why: [
      { icon: '👥', text: 'Manage membership programs and enrolled members' },
      { icon: '💳', text: 'Track contributions, payment dues, and membership status' },
      { icon: '🔗', text: 'Connect membership contributions back to their orders' },
    ],
    pricing: { model: 'connect', label: 'Pricing TBC', detail: 'Manage membership programs and contribution history in one place.' },
  },
  {
    key_name: 'reward_programs',
    title: 'Rewards',
    description: 'Create reward programs with scopes, rules, and reusable reward recipes.',
    icon: '🎁',
    optIn: true,
    why: [
      { icon: '🎯', text: 'Define affiliate, referral, regional, or store reward programs' },
      { icon: '⚙️', text: 'Build rules from reusable commission and reward components' },
      { icon: '📒', text: 'Keep reward calculation and ledger delivery managed by the ERP backend' },
    ],
    pricing: { model: 'connect', label: 'Pricing TBC', detail: 'Configure reward programs and connect them to attribution.' },
  },
  {
    key_name: 'tradly_attribution',
    title: 'Tradly Attribution',
    description: 'Understand which channels, campaigns, and partners drive your marketplace growth.',
    icon: '🎯',
    beta: true,
    why: [
      { icon: '📈', text: 'Measure the performance of acquisition channels and campaigns' },
      { icon: '🔗', text: 'Connect referrals and partner activity to marketplace outcomes' },
      { icon: '🧭', text: 'Use clear attribution insights to decide where to invest next' },
    ],
    pricing: { model: 'connect', label: 'Connect', detail: 'Open Tradly Attribution to measure campaign and partner performance.' },
  },
  {
    key_name: 'bundles_app',
    title: 'Bundles',
    description: 'Create fixed and mix-and-match bundles from existing listings.',
    icon: '🧺',
    optIn: true,
    entitlement: 'bundle_operations',
    why: [
      { icon: '🧩', text: 'Combine existing listings without duplicating product data' },
      { icon: '🎯', text: 'Support fixed bundles and customer-selected mix-and-match bundles' },
      { icon: '📦', text: 'Validate component stock before checkout and fulfilment' },
    ],
    pricing: { model: 'connect', label: 'Paid package feature', detail: 'Available on plans that include bundle operations.' },
  },
  {
    key_name: 'inventory_section',
    title: 'Supply Chain',
    optIn: true,
    description: 'Inventory, purchase orders, stock locations, suppliers, and operations.',
    icon: '📦',
    why: [
      { icon: '🏭', text: 'Track stock levels across multiple warehouses and store locations' },
      { icon: '🚚', text: 'Raise purchase orders with suppliers and receive stock against them' },
      { icon: '🔄', text: 'Record inventory operations — adjustments, transfers, and write-offs' },
    ],
    pricing: { model: 'connect', label: 'Included', detail: 'Supply Chain is included in your plan. No additional charge.' },
  },
  {
    key_name: 'people_app',
    title: 'People',
    description: 'Staff directory, departments, job profiles, and pay grades.',
    icon: '👥',
    optIn: true,
    why: [
      { icon: '🗂️', text: 'Keep every staff profile, code, and employment type in one directory' },
      { icon: '🏢', text: 'Organise teams into departments with standard job profiles' },
      { icon: '💼', text: 'Define pay grades once and assign them to staff' },
    ],
    pricing: { model: 'connect', label: 'Pricing TBC', detail: 'Required by Workforce and Payroll.' },
  },
  {
    key_name: 'workforce_app',
    title: 'Workforce',
    description: 'Shifts, attendance, and leave management for your team.',
    icon: '🗓️',
    optIn: true,
    requires: ['people_app'],
    why: [
      { icon: '📅', text: 'Roster staff into shifts by date, time, and location' },
      { icon: '⏱️', text: 'Clock in and out with a full attendance history' },
      { icon: '🌴', text: 'Leave policies, balances, and an approve/reject workflow' },
    ],
    pricing: { model: 'connect', label: 'Pricing TBC', detail: 'Turns on People automatically.' },
  },
  {
    key_name: 'payroll_app',
    title: 'Payroll',
    description: 'Payroll runs, approvals, CSV export, and expense reimbursements.',
    icon: '💰',
    optIn: true,
    requires: ['people_app'],
    why: [
      { icon: '🧾', text: 'Create payroll runs per period from staff pay grades' },
      { icon: '✅', text: 'Approve runs and export them as CSV for your bank or accountant' },
      { icon: '💳', text: 'Staff expense claims from submission to reimbursement' },
    ],
    pricing: { model: 'connect', label: 'Pricing TBC', detail: 'Turns on People automatically.' },
  },
  {
    key_name: 'expenses_app',
    title: 'Expenses',
    description: 'Capture staff expenses, approvals, reimbursements, and finance-ready records.',
    icon: '🧾',
    optIn: true,
    requires: ['people_app'],
    why: [
      { icon: '📸', text: 'Collect expense claims with receipts and supporting details' },
      { icon: '✅', text: 'Route claims through a clear approval workflow' },
      { icon: '💸', text: 'Track reimbursements and keep finance records organised' },
    ],
    pricing: { model: 'connect', label: 'Pricing TBC', detail: 'Turns on People automatically.' },
  },
  {
    key_name: 'recurring_payments_app',
    title: 'Recurring Payments',
    description: 'Set up and monitor recurring payment schedules for customers and subscriptions.',
    icon: '🔁',
    optIn: true,
    why: [
      { icon: '🔄', text: 'Create repeat payment schedules without manual follow-up' },
      { icon: '📅', text: 'Track upcoming, successful, and failed recurring charges' },
      { icon: '🔔', text: 'Keep your team informed about payment schedule changes' },
    ],
    pricing: { model: 'connect', label: 'Pricing TBC', detail: 'Manage recurring payment schedules from a dashboard mini app.' },
  },
  {
    key_name: 'chat_inbox',
    title: 'Customer Chat',
    description: 'All your customer communication — orders, support, and chat — from one place.',
    icon: '📬',
    sidebar: false,
    why: [
      { icon: '💬', text: 'Order queries, support tickets, and buyer messages in one thread view' },
      { icon: '⚡', text: 'Reply with AI-suggested responses trained on your catalogue' },
      { icon: '🏷️', text: 'Tag, assign, and resolve conversations across your team' },
    ],
    pricing: { model: 'connect', label: 'Connect', detail: 'Task-based pricing per resolved conversation.' },
  },
  {
    key_name: 'social_connect',
    title: 'Social Connect',
    description: 'Share listings to social channels on a schedule you define.',
    icon: '📡',
    beta: true,
    why: [
      { icon: '📱', text: 'Schedule listing posts to Instagram, Facebook, and X automatically' },
      { icon: '🗓️', text: 'Define posting schedules per channel — daily drops, flash sales, or custom cadence' },
      { icon: '📈', text: 'Track clicks and engagement back to each listing from the dashboard' },
    ],
    pricing: { model: 'connect', label: 'Connect', detail: 'Task-based pricing per scheduled post delivered.' },
  },
  {
    key_name: 'shopping_channels',
    title: 'Shopping Channels',
    description: 'Connect your catalogue to TikTok Shop, Shopee, Meta Catalog, Uber Eats, and more.',
    icon: '🛍️',
    comingSoon: true,
    why: [
      { icon: '🛒', text: 'Sync your listings to TikTok Shop, Shopee, and Meta Catalog instantly' },
      { icon: '🍔', text: 'Connect food & beverage catalogues to Uber Eats and Grab' },
      { icon: '🔄', text: 'Inventory and orders sync back automatically — no double-entry' },
    ],
    pricing: { model: 'connect', label: 'Connect', detail: 'Task-based pricing per order fulfilled through a connected channel.' },
  },
];

const MINI_APP_ENTITLEMENTS = Object.fromEntries(
  MINI_APPS.filter(app => app.entitlement).map(app => [app.key_name, app.entitlement]),
);

const MiniAppsModal = ({onClose}) => {
  const {translate: t} = useLocale();
  const appText = (app, field, fallback) => t(`navigation.mini_apps.${app.key_name}.${field}`, fallback ?? app[field]);
  const [states, setStates] = React.useState({});
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(null);
  const [expanded, setExpanded] = React.useState(null);

  React.useEffect(() => {
    (async () => {
      const config = await getConfigsByKey('superadmin');
      const saved = config?.sidebar_setup ?? [];
      const recommended = config?.recommended_apps ?? [];
      const initial = {};
      MINI_APPS.forEach(app => {
        const entry = saved.find(s => s.key_name === app.key_name);
        // Opt-in apps: the owner's own choice wins, otherwise on when signup recommended it
        initial[app.key_name] = app.optIn
          ? entry ? entry.feature_activated === true : recommended.includes(app.key_name)
          : entry ? entry.feature_activated !== false : true;
      });
      setStates(initial);
      setLoading(false);
    })();
  }, []);

  const toggleApp = async (key, currentValue) => {
    const entitlement = MINI_APP_ENTITLEMENTS[key];
    if (entitlement && AppConstants.restrictedFeatureArray.includes(entitlement)) {
      window.location.assign('/workspace/plans?package=marketplace');
      return;
    }
    const next = !currentValue;
    setStates(s => ({...s, [key]: next}));
    setSaving(key);
    const config = await getConfigsByKey('superadmin');
    const existing = config?.sidebar_setup ?? [];
    const updated = [...existing];
    const setActive = (k, value) => {
      const idx = updated.findIndex(s => s.key_name === k);
      const entry = {key_name: k, feature_activated: value};
      if (idx >= 0) updated[idx] = {...updated[idx], ...entry};
      else updated.push(entry);
    };
    setActive(key, next);
    if (next) {
      MINI_APPS.find(a => a.key_name === key)?.requires?.forEach(dep => setActive(dep, true));
    }
    await SaveConfigsDataByKey({
      config_key_group: 'superadmin',
      config_key_name: 'sidebar_setup',
      value: updated,
    });
    setSaving(null);
    window.location.reload();
  };

  const rows = [];
  for (let i = 0; i < MINI_APPS.length; i += 3) rows.push(MINI_APPS.slice(i, i + 3));
  const expandedApp = MINI_APPS.find(a => a.key_name === expanded);

  return (
    <Modal onCloseModal={onClose}>
      <div
        className="mini-apps-modal w-[calc(100vw-32px)] max-w-[1080px] max-h-[88vh] rounded-2xl shadow-2xl overflow-hidden flex flex-col"
        style={{background: 'var(--color-bg)', border: '1px solid var(--color-border)'}}>

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5" style={{borderBottom: '1px solid var(--color-border)'}}>
          <div>
            <h2 className="text-lg font-bold" style={{color: 'var(--color-text)'}}>{t('navigation.mini_apps.title', 'Mini Apps')}</h2>
            <p className="mini-app-muted text-xs mt-0.5" style={{color: 'var(--color-text-secondary)'}}>{t('navigation.mini_apps.subtitle', 'Tradly-built companion tools — tap a card to see details and pricing')}</p>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 transition-colors"
            style={{color: 'var(--color-text-secondary)', background: 'var(--color-surface)'}}>
            <XMarkIcon style={{width: 18, height: 18}} />
          </button>
        </div>

        {/* Content */}
        <div className="p-5 overflow-y-auto flex flex-col gap-2">
          {loading ? (
            <div className="grid grid-cols-3 gap-3">
              {[0,1,2,3,4,5].map(i => (
                <div key={i} className="h-36 rounded-2xl animate-pulse" style={{background: 'var(--color-surface)'}} />
              ))}
            </div>
          ) : rows.map((row, rowIdx) => {
            const expandedInRow = row.find(a => a.key_name === expanded);
            return (
              <div key={rowIdx} className="flex flex-col gap-2">
                {/* Card row */}
                <div className="grid grid-cols-3 gap-3">
              {row.map((app, appIndex) => {
                    const isActive = states[app.key_name];
                    const entitlement = app.entitlement || MINI_APP_ENTITLEMENTS[app.key_name];
                    const isLocked = entitlement && AppConstants.restrictedFeatureArray.includes(entitlement);
                    const isSaving = saving === app.key_name;
                    const isExpanded = expanded === app.key_name;
                    return (
                      <div
                        key={`app-${app.key_name || app.id || app.title || 'item'}-${rowIdx}-${appIndex}`}
                        className="relative flex flex-col rounded-2xl p-4 transition-all"
                        style={{
                          background: 'var(--color-surface)',
                          border: isExpanded
                            ? '1.5px solid var(--mini-app-active-border)'
                            : app.sidebar !== false && isActive && !app.comingSoon
                              ? '1.5px solid var(--mini-app-active-border)'
                              : '1.5px solid var(--color-border)',
                          opacity: app.comingSoon || isLocked ? 0.65 : 1,
                        }}>

                        {/* Active checkmark */}
                        {app.sidebar !== false && isActive && !app.comingSoon && !isExpanded && (
                          <div
                            className="absolute top-3 right-3 w-5 h-5 rounded-full flex items-center justify-center"
                            style={{background: 'var(--color-accent)'}}>
                            <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                              <path d="M1 4L3.5 6.5L9 1" stroke="var(--color-bg)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </div>
                        )}

                        {/* Expand chevron */}
                        {isExpanded && (
                          <div className="absolute top-3 right-3" style={{color: 'var(--color-accent)'}}>
                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                              <path d="M3 9L7 5L11 9" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </div>
                        )}

                        {/* Coming soon badge */}
                        {app.comingSoon && !isExpanded && (
                          <span
                            className="absolute top-3 right-3 text-[10px] font-semibold px-2 py-0.5 rounded-full"
                            style={{background: 'var(--color-input)', color: 'var(--color-text-secondary)'}}>
                            {t('navigation.mini_apps.soon', 'Soon')}
                          </span>
                        )}

                        {/* Icon */}
                        <div
                          className="w-11 h-11 rounded-xl flex items-center justify-center text-2xl mb-3"
                          style={{background: 'var(--color-input)'}}>
                          {app.icon}
                        </div>

                        <div className="mb-1 flex items-center gap-2">
                          <p className="text-sm font-semibold leading-snug" style={{color: 'var(--color-text)'}}>{appText(app, 'title')}</p>
                          {app.beta && <span className="rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide" style={{background: 'var(--color-input)', color: 'var(--color-accent)', border: '1px solid var(--mini-app-active-border)'}}>Beta</span>}
                        </div>
                        <p className="mini-app-description text-[11px] leading-relaxed" style={{color: 'var(--color-text-secondary)'}}>{appText(app, 'description')}</p>
                        {isLocked && <p className="mini-app-muted mt-2 text-[11px] font-semibold" style={{color: 'var(--color-text-secondary)'}}>{t('navigation.mini_apps.included_in_paid_plan', 'Included in a paid plan')}</p>}

                        {/* Know more */}
                        <button
                          onClick={e => { e.stopPropagation(); setExpanded(isExpanded ? null : app.key_name); }}
                          className="mt-2 flex items-center gap-1 text-[11px] font-medium self-start"
                          style={{color: 'var(--color-accent)'}}>
                          {isExpanded ? t('navigation.mini_apps.hide_details', 'Hide details') : t('navigation.mini_apps.know_more', 'Know more')}
                          <svg width="10" height="10" viewBox="0 0 10 10" fill="none" style={{transform: isExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s'}}>
                            <path d="M2 4L5 7L8 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </button>

                        {!app.comingSoon && app.sidebar !== false && (
                          <button
                            onClick={e => { e.stopPropagation(); !isSaving && toggleApp(app.key_name, isActive); }}
                            disabled={isSaving}
                            className="mt-2 w-full text-xs font-semibold py-1.5 rounded-lg transition-all"
                            style={isLocked ? {
                              background: 'transparent',
                              color: 'var(--color-text-secondary)',
                              border: '1px solid var(--color-border)',
                            } : isActive ? {
                              background: 'var(--color-input)',
                              color: 'var(--color-text)',
                            } : {
                              background: 'var(--color-accent)',
                              color: 'var(--color-bg)',
                            }}>
                            {isSaving ? '…' : isLocked ? t('navigation.mini_apps.view_plans', 'View plans') : isActive ? t('navigation.mini_apps.remove', 'Remove') : t('navigation.mini_apps.add_to_sidebar', 'Add to sidebar')}
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Detail panel — expands below the row */}
                {expandedInRow && (
                  <div
                    className="rounded-2xl p-5"
                    style={{background: 'var(--color-surface)', border: '1.5px solid var(--mini-app-active-border)'}}>
                    <div className="flex gap-6">

                      {/* Why you need it */}
                      <div className="flex-1 min-w-0">
                        <p className="mini-app-muted text-xs font-semibold uppercase tracking-wider mb-3" style={{color: 'var(--color-text-secondary)'}}>{t('navigation.mini_apps.why_heading', 'Why your business needs it')}</p>
                        <div className="flex flex-col gap-2.5">
                          {expandedInRow.why.map((item, i) => (
                            <div key={i} className="flex items-start gap-2.5">
                              <span className="text-base shrink-0 mt-0.5">{item.icon}</span>
                              <p className="text-xs leading-relaxed" style={{color: 'var(--color-text)'}}>{t(`navigation.mini_apps.${expandedInRow.key_name}.why_${i + 1}`, item.text)}</p>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Pricing */}
                      <div
                        className="w-44 shrink-0 rounded-xl p-4 flex flex-col gap-2"
                        style={{background: 'var(--color-input)', border: '1px solid var(--color-border)'}}>
                        <div className="flex items-center gap-1.5">
                          <span className="text-base">
                            {expandedInRow.pricing.model === 'credits' ? '🪙' : '🔗'}
                          </span>
                          <span className="text-xs font-bold" style={{color: 'var(--color-text)'}}>
                            {appText(expandedInRow, 'pricing_label', expandedInRow.pricing.label)}
                          </span>
                        </div>
                        <p className="mini-app-description text-[11px] leading-relaxed" style={{color: 'var(--color-text-secondary)'}}>
                          {appText(expandedInRow, 'pricing_detail', expandedInRow.pricing.detail)}
                        </p>
                        {expandedInRow.comingSoon && (
                          <span
                            className="mt-1 self-start text-[10px] font-semibold px-2 py-0.5 rounded-full"
                            style={{background: 'var(--color-surface)', color: 'var(--color-text-secondary)'}}>
                            {t('navigation.mini_apps.coming_soon', 'Coming soon')}
                          </span>
                        )}
                      </div>

                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </Modal>
  );
};

const SideMenubar = ({isExpandSet, extra_styles, isConfigReady = true}) => {
  const navigate = useNavigate();
  const {isDark} = useTheme();
  const {translate: t} = useLocale();
  const sideMenuSetup = CONFIG_CONSTANT.SUPERADMIN?.sidebar_setup ?? [];
  // Optional apps Owl picked from the signup prompt (superadmin.recommended_apps)
  const recommendedApps = CONFIG_CONSTANT.SUPERADMIN?.recommended_apps ?? [];
  const dynamicSideMenuSetup =
    CONFIG_CONSTANT.SUPERADMIN?.dynamic_sidebar_setup ?? [];
  const defaultExtensionMenu = CONFIG_CONSTANT?.DEFAULT_EXTENSION ?? [];

  const isMenuEnabled = useCallback(
    item => {
      const entitlement = item?.entitlement || MINI_APP_ENTITLEMENTS[item?.key_name];
      if (entitlement && AppConstants.restrictedFeatureArray.includes(entitlement)) {
        return false;
      }
      // Channel entries are independently configurable. Some older menu
      // definitions do not carry `is_channel`, so use the stable key set
      // instead of relying on that optional metadata.
      const isConfigurableChannel = CHANNEL_MENU_KEYS.has(item?.key_name);
      if (item?.opt_in) {
        // The owner's own on/off wins; otherwise shown when the signup prompt recommended it
        const setup = sideMenuSetup.find(entry => entry?.key_name === item.key_name);
        if (setup) {
          return setup.feature_activated === true;
        }
        return recommendedApps.includes(item.key_name);
      }
      if (
        item?.type === 'menu_group' ||
        (isConfigurableChannel &&
          !sideMenuSetup.some(setup => setup?.key_name === item.key_name)) ||
        sideMenuSetup.length === 0
      ) {
        return true;
      }

      const exact_setup = sideMenuSetup.find(
        setup => setup?.key_name === item.key_name,
      );
      if (exact_setup) {
        // Older tenants may have saved entries without this flag. Those
        // entries represent enabled defaults and must remain visible.
        return exact_setup.feature_activated !== false;
      }

      return sideMenuSetup.some(
        setup =>
          setup?.parent_key === item.key_name &&
          setup?.feature_activated !== false,
      );
    },
    [sideMenuSetup, recommendedApps],
  );

  const menus = useMemo(() => {
    const baseMenus = AppConstants.appType ? B2CMenus : AllMenus;
    return [
      ...baseMenus.filter(isMenuEnabled),
      ...dynamicSideMenuSetup,
      ...defaultExtensionMenu,
    ];
  }, [isMenuEnabled, dynamicSideMenuSetup, defaultExtensionMenu]).filter(
    (item, index, arr) =>
      arr.findIndex(m =>
        (m.key_name && m.key_name === item.key_name) ||
        (m.href && m.href === item.href)
      ) === index,
  );

  // Keep Commerce as a visual grouping only. The underlying menu objects stay
  // flat so saved feature keys, configuration screens, and search retain the
  // existing menu contract.
  const presentationMenus = useMemo(() => addPresentationGroups(menus), [menus]);

  const [isExpand, setIsExpand] = useState(isExpandSet ?? true);
  const [showTenantList, setShowTenantList] = useState(false);
  const [showMiniApps, setShowMiniApps] = useState(false);

  const color_groups_list = useMemo(
    () => AllMenus.filter(it => it.type === 'color'),
    [],
  );
  const background_color = sideMenuSetup.find(
    mn => mn.key_name === 'background_color',
  );
  const text_color = sideMenuSetup.find(mn => mn.key_name === 'text_color');
  const dark_background_color = sideMenuSetup.find(
    mn => mn.key_name === 'dark_background_color',
  );
  const dark_text_color = sideMenuSetup.find(
    mn => mn.key_name === 'dark_text_color',
  );

  const menuBackgroundColor = isDark
    ? dark_background_color?.value ?? '#1e1e1e'
    : background_color?.value ?? '#01544E';
  const menuTextColor = isDark
    ? dark_text_color?.value ?? '#ffffff'
    : text_color?.value ?? '#ffffff';

  useEffect(() => {
    document.documentElement.style.setProperty('--sidebar-bg', menuBackgroundColor);
    document.documentElement.style.setProperty('--sidebar-text', menuTextColor);
  }, [menuBackgroundColor, menuTextColor]);

  return (
    <>
    <div
      className={`dark-side-menubar relative  h-[100dvh]  overflow-visible flex flex-col py-2 ${
        isExpand ? 'w-[210px]' : 'w-14'
      } transition-all duration-300 ${extra_styles}`}
      style={{
        position: 'relative',
        height: '100vh',
        overflowY: 'hidden',
        overflowX: 'visible',
        display: 'flex',
        flexDirection: 'column',
        paddingTop: 8,
        paddingBottom: 8,
        width: isExpand ? 210 : 56,
        borderRight: 'none',
        background: menuBackgroundColor,
      }}>
      {/* Header: tenant switcher + sidebar toggle */}
      <div className="hidden md:flex flex-col w-full sticky top-0 z-10 pb-2">

        {/* Logo mark */}
        <div className={`flex items-center px-3 pt-2 pb-1 ${isExpand ? 'gap-2.5' : 'justify-center'}`}>
          <TradlySidebarLogo width={34} height={34} style={{flexShrink: 0, color: menuTextColor}} aria-label="Tradly" />
          {isExpand && (
            <span className="text-base font-extrabold tracking-tight truncate" style={{color: menuTextColor}}>
              Tradly
            </span>
          )}
        </div>

        <div className={`flex items-center gap-2 px-1.5 py-1 ${isExpand ? 'justify-between' : 'justify-center'}`}>
          {isExpand && (
            <button
              className="flex flex-1 items-center gap-1.5 min-w-0 rounded-lg px-2 py-1.5 hover:bg-white/10 transition-all duration-150"
              style={{color: menuTextColor}}
              onClick={() => setShowTenantList(true)}>
              <span className="truncate text-sm font-semibold">
                {AppConstants.tenantName || 'SuperAdmin'}
              </span>
              <ChevronUpDownIcon style={{width: 13, height: 13, opacity: 0.6, flexShrink: 0}} />
            </button>
          )}
          <button
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg hover:bg-white/10 transition-all duration-150"
            style={{color: menuTextColor}}
            onClick={() => setIsExpand(!isExpand)}
            title={isExpand ? t('navigation.collapse_sidebar', 'Collapse sidebar') : t('navigation.expand_sidebar', 'Expand sidebar')}>
            <ViewColumnsIcon style={{width: 20, height: 20}} strokeWidth={1.5} />
          </button>
        </div>

        {/* Search */}
        {isExpand ? (
          <div className="px-1.5 pb-1">
            <TopSearcher sidebar={true} sidebarTextColor={menuTextColor} />
          </div>
        ) : (
          <div className="flex justify-center pb-1">
            <button
              className="flex h-9 w-9 items-center justify-center rounded-lg hover:bg-white/10 transition-all duration-150"
              style={{color: menuTextColor}}
              title={t('navigation.search', 'Search')}>
              <MagnifyingGlassIcon style={{width: 18, height: 18}} strokeWidth={1.5} />
            </button>
          </div>
        )}
      </div>

      {/* Menus */}
      <div
        className="flex flex-col min-h-0 flex-1 mt-2 overflow-y-auto"
        aria-busy={!isConfigReady}>
        {/* The canonical menu is local. Tenant-specific visibility settings
            are applied progressively and must never block navigation. */}
        {presentationMenus
          ?.filter(
            item =>
              ![15, 17, 18].includes(item.id) &&
              item.isShow &&
              item.type !== 'color',
          )
          ?.map((item, itemIndex) => {
            if (item.type === 'menu_group') {
              const isAppsGroup = item.key_name === 'apps_group';
              const isChannelsGroup = item.key_name === 'channels_group';
              return (
                <div
                  key={`menu-group-${item.key_name || item.id || item.title || 'item'}-${itemIndex}`}
                  className={`mx-3 mt-4 mb-1 ${isExpand ? 'px-1' : 'px-0'} relative`}
                  style={{color: menuTextColor}}
                  aria-label={t(item.key_name === 'apps_group' ? 'navigation.apps_group' : item.key_name === 'channels_group' ? 'navigation.channels_group' : `navigation.${item.key_name}`, item.title)}>
                  {isExpand && (
                    <div className="flex items-center justify-between">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.16em] opacity-70">
                        {t(item.key_name === 'apps_group' ? 'navigation.apps_group' : item.key_name === 'channels_group' ? 'navigation.channels_group' : `navigation.${item.key_name}`, item.title)}
                      </p>
                      {(isAppsGroup || isChannelsGroup) && (
                        <button
                          onClick={() => {
                            if (isAppsGroup) {
                              setShowMiniApps(v => !v);
                            } else {
                              navigate('/manage_lfn');
                            }
                          }}
                          className={`flex h-6 w-6 items-center justify-center rounded-full transition-all hover:scale-105 hover:opacity-90 ${isAppsGroup ? 'shadow-sm' : 'hover:bg-white/10'}`}
                          style={{
                            background: isAppsGroup ? '#2DC4B8' : 'transparent',
                            border: isAppsGroup ? '1px solid rgba(255,255,255,0.18)' : 'none',
                            boxShadow: isAppsGroup ? '0 2px 8px rgba(0,0,0,0.16)' : 'none',
                            color: isAppsGroup ? '#fff' : menuTextColor,
                          }}
                          aria-label={isAppsGroup ? t('navigation.add_or_manage_mini_apps', 'Add or manage Mini Apps') : t('navigation.manage_channels', 'Manage Channels')}
                          title={isAppsGroup ? t('navigation.manage_mini_apps', 'Manage Mini Apps') : t('navigation.manage_channels', 'Manage Channels')}>
                          <PlusIcon style={{width: 14, height: 14}} strokeWidth={2.2} />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            }
            if (item.type === 'presentation_group') {
              return (
                <CommerceAccordion
                  key={item.key_name || item.id}
                  item={item}
                  isExpand={isExpand}
                  text_color={menuTextColor}
                  background_color={menuBackgroundColor}
                />
              );
            }
            return (
              <SideMenu
                key={`menu-${item.key_name || item.id || item.title || 'item'}-${itemIndex}`}
                menu={item}
                isExpand={isExpand}
                text_color={menuTextColor}
                background_color={menuBackgroundColor}
              />
            );
          })}
      </div>

      {/* Sticky bottom strip */}
      <div
        className="flex items-center gap-0.5 px-1.5 py-1 border-t border-white/10 flex-shrink-0"
        style={{background: menuBackgroundColor, color: menuTextColor}}>
        {/* Invite Team */}
        <button
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg hover:bg-white/10 transition-all duration-150"
          title={t('navigation.invite_team', 'Invite Team')}
          onClick={() => navigate('/workspace/user-management')}>
          <UserPlusIcon style={{width: 18, height: 18}} strokeWidth={1.5} />
        </button>
        {/* Help — hover reveals flyout */}
        <div className="group relative">
          <button
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg hover:bg-white/10 transition-all duration-150"
            title={t('navigation.help', 'Help')}>
            <QuestionMarkCircleIcon style={{width: 18, height: 18}} strokeWidth={1.5} />
          </button>
          <div
            // Keep the flyout touching the Help button so the pointer does
            // not cross a dead gap and collapse the menu before selection.
            className="pointer-events-none absolute bottom-9 left-0 hidden group-hover:block group-hover:pointer-events-auto z-50"
            style={{filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.25))'}}>
            <div className="w-52 rounded-xl overflow-hidden py-2" style={{background: menuBackgroundColor}}>
              {fixed_menus.find(m => m.title === 'Help')?.sub_menus?.map((sub, subIndex) => (
                <button
                  key={`help-${sub.id || sub.key_name || sub.title || 'item'}-${subIndex}`}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left hover:bg-white/10 transition-all duration-150"
                  style={{color: menuTextColor}}
                  onClick={() => window.open(sub.href, '_blank')}>
                  <QuestionMarkCircleIcon style={{width: 16, height: 16, opacity: 0.7}} strokeWidth={1.5} />
                  {t(`navigation.help_${String(sub.title).toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '')}`, sub.title)}
                </button>
              ))}
            </div>
          </div>
        </div>
        {/* Settings */}
        <button
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg hover:bg-white/10 transition-all duration-150"
          title={t('navigation.settings', 'Settings')}
          onClick={() => navigate('/settings')}>
          <Cog6ToothIcon style={{width: 18, height: 18}} strokeWidth={1.5} />
        </button>
        {/* AI Credits */}
        <AICreditsControl />
        <div className="flex-1" />
      </div>
    </div>

    {showTenantList && (
      <Modal onCloseModal={() => setShowTenantList(false)}>
        <div className="max-w-[600px] max-h-[90dvh] absolute right-0 left-0 md:top-0 bottom-0 mx-auto my-auto w-full h-full grid place-items-start bg-white backdrop-blur-sm z-50 rounded-md">
          <TenantsList closeModal={() => setShowTenantList(false)} />
        </div>
      </Modal>
    )}
    {showMiniApps && (
      <MiniAppsModal onClose={() => setShowMiniApps(false)} />
    )}
    </>
  );
};

export default SideMenubar;
